import React from "react";
import { Sparkles } from "lucide-react";

interface VelaBannerProps {
  reviewCount: number;
  autoCount: number;
  onReviewClick: () => void;
}

/**
 * One compact Vela banner for the Bank transactions dashboard.
 * Max 540px, left-aligned, whole banner is clickable when there's something to review.
 * Hides when nothing to say. No cheap letter avatar; small Sparkles badge instead.
 */
export function VelaBanner({ reviewCount, autoCount, onReviewClick }: VelaBannerProps) {
  if (reviewCount === 0 && autoCount === 0) return null;

  const hasReview = reviewCount > 0;
  const parts: string[] = [];
  if (reviewCount > 0) parts.push(`${reviewCount} to review`);
  if (autoCount > 0) parts.push(`${autoCount} auto-matched this month`);

  const common =
    "inline-flex items-center gap-3 bg-vela-fade rounded-lg px-3.5 py-2.5 mb-5 max-w-xl w-full md:w-auto text-left";

  const content = (
    <>
      <span className="w-7 h-7 rounded-md bg-vela text-white inline-flex items-center justify-center flex-shrink-0">
        <Sparkles size={14} strokeWidth={2.5} />
      </span>
      <span className="text-[13px] text-ink2 flex-1 min-w-0">
        <strong className="text-vela-deep font-bold">
          {hasReview ? `${reviewCount} for you` : "All caught up"}
        </strong>
        {parts.length > 0 && <>, {parts.join(", ")}.</>}
      </span>
      {hasReview && (
        <span className="bg-vela-deep text-white rounded-md px-3 py-1.5 text-[12px] font-semibold flex-shrink-0">
          Review
        </span>
      )}
    </>
  );

  if (!hasReview) {
    return <div className={common}>{content}</div>;
  }

  return (
    <button
      type="button"
      onClick={onReviewClick}
      className={`${common} cursor-pointer hover:brightness-95 transition`}
    >
      {content}
    </button>
  );
}